// src/components/common/TypeToConfirmModal.tsx
/**
 * A destructive-action confirmation that requires typing a word before it will proceed.
 *
 * ⚠️ Used for **bulk** actions specifically. A single row can be undone by hand; a bulk action over a
 * selection cannot be un-selected after the fact, and the person confirming usually cannot see every
 * affected row at once. Typing the word is the point — it defeats the muscle memory that makes a plain
 * "Are you sure?" a no-op.
 *
 * The word is a prop rather than hardcoded to "delete" so the ceremony always names **what actually
 * happens**. A dialog that asks you to type "delete" for an action that cancels is training people to
 * confirm things they have not read.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

export interface TypeToConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  title: string;
  /** The word the admin must type, case-insensitively. Name the real action. */
  confirmWord: string;
  /** What is about to happen, in plain words. */
  warning: React.ReactNode;
  /** How many records are affected. Shown so the number is never a surprise. */
  count: number;
  /** Label for the confirming button. */
  actionLabel: string;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
}

export default function TypeToConfirmModal({
  opened, onClose, title, confirmWord, warning, count, actionLabel, busy = false, onConfirm,
}: TypeToConfirmModalProps) {
  const [typed, setTyped] = useState('');

  useEffect(() => { if (opened) setTyped(''); }, [opened]);

  const matches = typed.trim().toLowerCase() === confirmWord.trim().toLowerCase();

  return (
    <Modal
      opened={opened}
      // Locked while working: closing mid-run would leave the admin unsure how far it got.
      onClose={busy ? () => {} : onClose}
      title={title}
      size="md"
      centered
      //  The header "X" is rendered by Modal itself, outside the body <div> below, so it needs its
      // own stopPropagation — the wrapper only catches clicks inside the body.
      closeButtonProps={{
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          if (!busy) onClose();
        },
      }}
    >
      {/*
         Mantine's Modal renders via a portal — its DOM sits outside whatever row/card opened it, but
        React's synthetic events bubble through the *component* tree, not the DOM tree. Without this
        wrapper, a click on Confirm/Cancel/the input still bubbles up through whichever row or card
        rendered this modal (e.g. a table row's `onRowClick`), firing that handler as a side effect of
        confirming a delete. Stopping propagation once, here, covers every control inside the modal —
        current and future — across all 4 call sites, without patching each one individually.
      */}
      <div onClick={(e) => e.stopPropagation()}>
        <Stack gap="md">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">{warning}</Text>
          </Alert>

          <Text size="sm">
            This affects <Text span fw={700}>{count}</Text>{' '}
            {count === 1 ? 'record' : 'records'}.
          </Text>

          <TextInput
            label={`Type ${confirmWord.toUpperCase()} to confirm`}
            placeholder={confirmWord.toUpperCase()}
            value={typed}
            onChange={(e) => setTyped(e.currentTarget.value)}
            disabled={busy}
            data-autofocus
          />

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              color="red"
              onClick={onConfirm}
              loading={busy}
              // ⚠️ Gated on the typed word, not merely on having opened the dialog.
              disabled={!matches || busy || count === 0}
            >
              {actionLabel}
            </Button>
          </Group>
        </Stack>
      </div>
    </Modal>
  );
}